import React, { useEffect, useState } from 'react';
import { useStowageStore } from 'src/context/StowageContext';
import { DefaultFlexStrechedWrapper } from 'src/components/ui/odfjellPanel/OdfjellPanel.styles';
import { AdjustedCargoTemperature } from 'src/types/ViewModel/Stowage/AdjustedCargoTemperature';
import OdfjellInputComponent, { InputChangeData } from 'src/components/common/odfjell-input-component/OdfjellInputComponent';
import { hasInvalidFields } from 'src/utils/aolFieldsHelper';
import { useStowageSelectionStore } from 'src/context/StowageSelectionContext';
import { getFormattedCargoNumber, isManualCargo } from 'src/utils/stowage/cargoOperationUtils';
import {
    CARGO_TEMPERATURE_MAX,
    CARGO_TEMPERATURE_MIN,
    getCargoTemperatureFromCommodityBook,
} from 'src/utils/stowage/stowageOperation/cargoTemperatureUtils';
import {
    getAdjustedCargoTemperature,
    getAdjustedCargoTemperaturesInTanks,
    updateAdjustedCargoTemperature,
} from 'src/utils/stowage/stowageOperation/adjustedCargoTemperatureUtils';
import { AdjustedStowageList, ElementWrapper, HighlightPanel, PanelSectionContent } from 'src/components/ui/odfjellPanel/OdfjellPanel.styles';
import OdfjellPanel from 'src/components/ui/odfjellPanel/OdfjellPanel';
import usePostStowageApi from 'src/api/stowage/usePostStowageApi';
import { StowageChangeDataType } from 'src/types/ViewModel/Stowage/Enums/StowageChangeDataType';
import { createStowagePlanChange } from 'src/factory/Stowage/StowagePlanChanges/StowagePlanChangeFactory';
import { deepCopyStowagePlan } from 'src/factory/Stowage/StowagePlanFactory';

type AdjustCargoTemperatureVM = {
    temperature: number;
};

const AdjustCargoTemperaturePanel: React.FC = () => {
    const [adjustCargoTemperatureVM, setAdjustCargoTemperatureVM] = useState<AdjustCargoTemperatureVM>(null);
    const { activeStowagePlan, setIsAdjustCargoTemperaturePanelOpen } = useStowageStore();
    const { selectedCargoOperation } = useStowageSelectionStore();
    const [hasEmptyValues, setHasEmptyValues] = useState<boolean>(false);
    const [adjustedStowageOperations, setAdjustedStowageOperations] = useState<AdjustedCargoTemperature[]>([]);
    const { saveStowageData } = usePostStowageApi();

    useEffect(() => {
        if (!activeStowagePlan || !selectedCargoOperation) {
            return;
        }
        setAdjustedStowageOperations(getAdjustedCargoTemperaturesInTanks(activeStowagePlan, selectedCargoOperation.cargo));
        setAdjustCargoTemperatureVM({
            temperature:
                getAdjustedCargoTemperature(activeStowagePlan, selectedCargoOperation.cargo)?.temperature ??
                getCargoTemperatureFromCommodityBook(selectedCargoOperation.cargo),
        });
    }, []);

    const onCancel = () => {
        setIsAdjustCargoTemperaturePanelOpen(false);
    };
    const isFormDataInvalid = (): boolean => {
        return false;
    };

    const onInputChange = (changeObject: InputChangeData) => {
        const name = changeObject.event.target.name;
        const value = changeObject.event.target.value;

        changeObject.dataObject[name] = value ? +value : 0;

        hasInvalidFields(adjustCargoTemperatureVM, name, changeObject.isValid);
        setAdjustCargoTemperatureVM({ ...adjustCargoTemperatureVM });
        if (hasEmptyValues) setHasEmptyValues(false);
    };

    const onSave = async () => {
        const adjustedCargoTemperature: AdjustedCargoTemperature = {
            adjustedCargoTemperatureGuid: crypto.randomUUID(),
            cargo: selectedCargoOperation.cargo,
            cargoGuid: selectedCargoOperation.cargoGuid,
            temperature: adjustCargoTemperatureVM.temperature,
        };

        const _stowagePlan = deepCopyStowagePlan(activeStowagePlan);
        const response = updateAdjustedCargoTemperature(_stowagePlan, adjustedCargoTemperature);
        const stowagePlanChanges = [
            createStowagePlanChange(
                _stowagePlan.stowagePlanGuid,
                response.adjustedCargoTemperatureGuid,
                StowageChangeDataType.AdjustedCargoTemperature,
                response.changedData
            ),
        ];

        await saveStowageData(_stowagePlan, stowagePlanChanges);
        setIsAdjustCargoTemperaturePanelOpen(false);
    };

    return (
        adjustCargoTemperatureVM &&
        selectedCargoOperation && (
            <OdfjellPanel
                headerTitle={
                    'Adjust temperature for cargo ' +
                    getFormattedCargoNumber(
                        selectedCargoOperation.cargo.voyageNumber,
                        selectedCargoOperation.cargo.cargoNumber,
                        isManualCargo(selectedCargoOperation.cargo)
                    )
                }
                defaultPosition="center"
                preventOutsideClose={true}
                onCancel={onCancel}
                onAction={onSave}
                showCancelButton={true}
                isActionDisabled={isFormDataInvalid()}
            >
                <DefaultFlexStrechedWrapper>
                    <PanelSectionContent>
                        <ElementWrapper>
                            <h4>The following tanks have specific cargo temperature:</h4>
                            <AdjustedStowageList className="adjusted-stowage-list">
                                {adjustedStowageOperations
                                    .filter((o) => o.tank)
                                    .map((op) => {
                                        return (
                                            <li className="stowage-item" key={'tank_' + op.tank.tankId}>
                                                <div>
                                                    <label>Tank</label>
                                                    <span>{op.tank.odfjellTankName}</span>
                                                </div>
                                                <div>
                                                    <label>Temperature</label>
                                                    <span>{op.temperature} °C</span>
                                                </div>
                                            </li>
                                        );
                                    })}
                            </AdjustedStowageList>
                        </ElementWrapper>
                    </PanelSectionContent>
                </DefaultFlexStrechedWrapper>
                <DefaultFlexStrechedWrapper>
                    <PanelSectionContent>
                        <HighlightPanel>
                            <ElementWrapper>
                                <h4>Adjust temperature</h4>
                                <OdfjellInputComponent
                                    isReadOnly={true}
                                    type={'number'}
                                    description="Product temperature in Commodity Book"
                                    value={getCargoTemperatureFromCommodityBook(selectedCargoOperation.cargo)}
                                    name={'productTemperature'}
                                    unit={'°C'}
                                    key={'input_product_temperature'}
                                />
                                <OdfjellInputComponent
                                    isReadOnly={false}
                                    type={'number'}
                                    description={'Cargo temperature'}
                                    value={adjustCargoTemperatureVM.temperature}
                                    min={CARGO_TEMPERATURE_MIN}
                                    max={CARGO_TEMPERATURE_MAX}
                                    name={'temperature'}
                                    dataObject={adjustCargoTemperatureVM}
                                    leaveBlank={true}
                                    unit={'°C'}
                                    inputSize="small"
                                    onChange={onInputChange}
                                    tabIndex={1}
                                    key={'input_cargo_temp'}
                                />
                            </ElementWrapper>
                        </HighlightPanel>
                    </PanelSectionContent>
                </DefaultFlexStrechedWrapper>
            </OdfjellPanel>
        )
    );
};

export default AdjustCargoTemperaturePanel;
